import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject, take, takeUntil } from 'rxjs';
import { GoogleMapService } from '../../services/google-map.service';
import { AddressType, OPTIONS } from './models/autocomplete-address';

let autocomplete: google.maps.places.Autocomplete;
let autocompleteLsr: google.maps.MapsEventListener;

@Component({
    standalone: true,
    selector: 'app-autocomplete-address',
    templateUrl: './autocomplete-address.component.html',
    styleUrls: ['./autocomplete-address.component.scss'],
    imports: [ReactiveFormsModule, FormsModule, MatFormFieldModule, MatInputModule],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class AutocompleteAddressComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;

    @Input() fieldName: string;
    @Input() placeholder = 'Enter your billing address';
    @Output() eventOnChangeAddress = new EventEmitter<AddressType>();

    @ViewChild('autocompleteAddress', { static: true })
    inputElement: ElementRef<HTMLInputElement>;

    constructor(private googleMapService: GoogleMapService) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.initAutoComplete();
        this.loadGoogleMapsAPI();
    }

    ngOnDestroy(): void {
        this.removeGoogleMapsListener();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    removeGoogleMapsListener(): void {
        google.maps.event.removeListener(autocompleteLsr);
        google.maps.event.clearInstanceListeners(autocomplete);
    }

    getAutocomplete(): google.maps.places.Autocomplete {
        return new google.maps.places.Autocomplete(this.inputElement.nativeElement, OPTIONS);
    }

    fillInAddress(autocomplete): void {
        const place = autocomplete.getPlace();
        let address1 = '';
        let postcode = '';
        const address: AddressType = {
            street: '',
            country: '',
            zipCode: '',
            state: '',
            city: '',
        };
        for (const component of place.address_components) {
            const componentType = component.types[0];
            switch (componentType) {
                case 'street_number': {
                    address1 = `${component.long_name} ${address1}`;
                    break;
                }

                case 'route': {
                    address1 += component.short_name;
                    address.street = address1;
                    break;
                }

                case 'postal_code': {
                    postcode = `${component.long_name}${postcode}`;
                    address.zipCode = postcode.replace(/[- ]/g, '');
                    break;
                }

                case 'postal_code_suffix': {
                    postcode = `${postcode}-${component.long_name}`;
                    break;
                }

                case 'locality':
                    address.city = component.long_name;
                    break;

                case 'administrative_area_level_1': {
                    address.state = component.long_name;
                    break;
                }

                case 'country':
                    address.country = component.short_name === 'US' ? 'United States of America' : 'Canada';
                    break;
            }
        }
        this.eventOnChangeAddress.emit(address);
    }

    loadGoogleMapsAPI(): void {
        this.googleMapService.loadAPI().pipe(take(1)).subscribe();
    }

    initAutoComplete(): void {
        this.googleMapService
            .isGoogleMapsLoaded()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isGoogleMapsLoaded) => {
                    if (isGoogleMapsLoaded) {
                        autocomplete = this.getAutocomplete();
                        autocompleteLsr = autocomplete.addListener('place_changed', () => {
                            this.fillInAddress(autocomplete);
                        });
                    }
                },
            });
    }
}
